import type { Language } from "@/hooks/useLanguage";

export type PrivacyLegal = {
  heroPill: string;
  title: string;
  subtitle: string;
  controllerTitle: string;
  controllerIntro: string;
  contactLabel: string;
  commercialRegister: string;
  vatStatus: string;
  s1Title: string;
  s1_1Title: string;
  s1_1Items: string[];
  s1_2Title: string;
  s1_2Items: string[];
  s1_3Title: string;
  s1_3Items: string[];
  s2Title: string;
  thPurpose: string;
  thLegalBasis: string;
  purposeRows: [string, string][];
  s3Title: string;
  s3Intro: string;
  thProcessor: string;
  thPurposeCol: string;
  thDataLocation: string;
  processorRows: [string, string, string][];
  s3Footer: string;
  s4Title: string;
  securityItems: [string, string][];
  s5Title: string;
  retentionItems: [string, string][];
  warnTitle: string;
  warnBody: string;
  s6Title: string;
  rightsIntro: string;
  rightsItems: string[];
  rightsComplaintBefore: string;
  rightsComplaintAfter: string;
  rightsContactBefore: string;
  rightsContactAfter: string;
  s7Title: string;
  s7p1: string;
  s7p2: string;
  s8Title: string;
  s8p1: string;
  s8p2: string;
  s9Title: string;
  s9p1: string;
  s10Title: string;
  emailLabel: string;
  closingPlaceDate: string;
};

const cs: PrivacyLegal = {
  heroPill: "Účinné od 25. října 2026",
  title: "Zásady ochrany osobních údajů",
  subtitle: "Platformy Zettl",
  controllerTitle: "Správce osobních údajů",
  controllerIntro: "Správcem osobních údajů je provozovatel platformy Zettl:",
  vatStatus: "neplátce DPH",
  contactLabel: "Kontakt:",
  commercialRegister:
    "Zapsaná v obchodním rejstříku vedeném Městským soudem v Praze, oddíl C, vložka 379847",
  s1Title: "1. Rozsah zpracovávaných údajů",
  s1_1Title: "1.1 Identifikační a kontaktní údaje",
  s1_1Items: [
    "Jméno, příjmení, e-mailová adresa",
    "Název společnosti, obchodní adresa (ulice, město, PSČ, země)",
    "IČO, DIČ, status plátce DPH",
    "Jazykové a měnové preference, preference zobrazení",
    "Při přihlášení přes Google: jméno, e-mailová adresa a profilový obrázek, které nám předá Google",
  ],
  s1_2Title: "1.2 Finanční údaje",
  s1_2Items: [
    "Záznamy o bankovních transakcích (datum, popis, částka, měna, kategorie)",
    "Faktury včetně položek, proforma faktury, dobropisy",
    "Soubory účtenek a extrahovaná metadata",
    "Přihlašovací údaje k bankovním API (šifrované tokeny)",
    "Záznamy o klientech (šifrované)",
    "Rozpočtové položky a záznamy o výdajích",
    "Fakturační údaje a historie plateb za předplatné (od spuštění plateb); údaje o platební kartě zpracovává přímo Stripe a Správce je nevidí",
  ],
  s1_3Title: "1.3 Technické a provozní údaje",
  s1_3Items: [
    "IP adresy (auditní záznamy)",
    "User agent řetězce (auditní záznamy)",
    "Protokoly zpracování e-mailů (odesílatel, předmět, počet příloh, stav)",
    "Protokoly chyb synchronizace",
    "Čítače rate limitů",
    "Chybová hlášení aplikace a záznamy průběhu relací, ve kterých nastala chyba, se skrytým textem (Sentry)",
    "Statistiky návštěvnosti a používání webu a aplikace, jen s vaším souhlasem (Google Analytics 4, PostHog)",
  ],
  s2Title: "2. Účely zpracování a právní základ",
  thPurpose: "Účel",
  thLegalBasis: "Právní základ (GDPR)",
  purposeRows: [
    ["Poskytování služeb Platformy", "Čl. 6 odst. 1 písm. b) — plnění smlouvy"],
    ["Registrace a správa účtu", "Čl. 6 odst. 1 písm. b) — plnění smlouvy"],
    ["Synchronizace bankovních účtů", "Čl. 6 odst. 1 písm. a) — souhlas"],
    ["AI zpracování dokladů", "Čl. 6 odst. 1 písm. b) — plnění smlouvy"],
    ["Bezpečnost a prevence zneužití", "Čl. 6 odst. 1 písm. f) — oprávněný zájem"],
    ["Auditní záznamy", "Čl. 6 odst. 1 písm. f) — oprávněný zájem"],
    ["Komunikace s uživatelem", "Čl. 6 odst. 1 písm. b) — plnění smlouvy"],
    [
      "Platby za předplatné a účetní doklady (od spuštění plateb)",
      "Čl. 6 odst. 1 písm. b) — plnění smlouvy; písm. c) — právní povinnost (účetnictví)",
    ],
    ["Odhalování a oprava chyb aplikace", "Čl. 6 odst. 1 písm. f) — oprávněný zájem"],
    ["Statistiky návštěvnosti a používání webu a aplikace", "Čl. 6 odst. 1 písm. a) — souhlas"],
  ],
  s3Title: "3. Příjemci a zpracovatelé osobních údajů",
  s3Intro: "Osobní údaje mohou být předány následujícím kategoriím příjemců (zpracovatelů):",
  thProcessor: "Zpracovatel",
  thPurposeCol: "Účel",
  thDataLocation: "Sídlo dat",
  processorRows: [
    ["Supabase, Inc.", "Databáze, přihlášení, úložiště souborů, serverové funkce", "EU (Irsko)"],
    [
      "Vercel Inc.",
      "Hosting webu, CDN, návštěvnost a rychlost webu (Vercel Analytics, Speed Insights), AI Gateway",
      "EU/USA",
    ],
    [
      "Google LLC (Gemini)",
      "Čtení dokladů a párování pomocí AI (přes Vercel AI Gateway)",
      "EU/USA",
    ],
    ["Google LLC (Fonts)", "Načítání webových fontů (Sora)", "EU/USA"],
    [
      "Cloudflare, Inc.",
      "Příjem e-mailů s doklady; šifrované noční zálohy (R2, uchovávané 30 dní)",
      "EU/USA",
    ],
    [
      "Cloudflare, Inc. (Turnstile)",
      "Ochrana přihlášení a formulářů před boty (CAPTCHA)",
      "EU/USA",
    ],
    [
      "Resend, Inc.",
      "Odesílání e-mailů (registrace a přihlášení, upozornění na konec zkušební doby, pozvánky do týmu, odpovědi na kontaktní formulář)",
      "EU/USA",
    ],
    ["Functional Software, Inc. (Sentry)", "Sledování chyb aplikace", "EU (Německo)"],
    [
      "Google Ireland Limited (Google Analytics 4)",
      "Statistiky návštěvnosti a používání webu a aplikace (jen s vaším souhlasem)",
      "EU/USA",
    ],
    [
      "PostHog, Inc.",
      "Statistiky používání webu a aplikace (jen s vaším souhlasem)",
      "EU (Německo)",
    ],
    [
      "GitHub, Inc.",
      "Spouštění noční zálohy (data jsou na běžci jen dočasně, před uložením se šifrují)",
      "USA",
    ],
    [
      "Stripe Payments Europe, Ltd.",
      "Platby za předplatné (od spuštění plateb); pro prevenci podvodů a své regulatorní povinnosti je Stripe samostatným správcem",
      "EU (Irsko)/USA",
    ],
  ],
  s3Footer:
    "Databáze a nahrané soubory jsou uloženy v EU (Irsko), chybová hlášení v EU (Německo). Někteří zpracovatelé jsou však společnosti se sídlem v USA nebo provozují celosvětovou infrastrukturu, takže u zpracovatelů označených „EU/USA“ nebo „USA“, a u zpracovatelů se sídlem v USA i tehdy, když jsou data uložena v EU, může docházet k předávání osobních údajů mimo EU. Předávání probíhá na základě rozhodnutí Evropské komise o odpovídající ochraně (EU-US Data Privacy Framework) u certifikovaných příjemců, jinak na základě standardních smluvních doložek (čl. 46 GDPR). Při načítání webových fontů z Google Fonts zpracovává Google LLC IP adresu návštěvníka. Na přihlašovacím a registračním formuláři používáme Cloudflare Turnstile: aby rozlišil lidi od botů, zpracovává Cloudflare IP adresu a technické údaje o prohlížeči a zařízení. Právním základem obojího je náš oprávněný zájem na zabezpečení a fungování služby (čl. 6 odst. 1 písm. f) GDPR). Google Analytics 4 a PostHog spouštíme jen tehdy, když s nimi v liště cookies souhlasíte (čl. 6 odst. 1 písm. a) GDPR). Pak mohou do vašeho prohlížeče ukládat cookies nebo jiné identifikátory a zpracovávají IP adresu, údaje o prohlížeči a zařízení a o tom, které stránky a funkce používáte. Se souhlasem k vaší firmě také uložíme kampaň (parametry utm) a název hostitele webu, které vás k registraci přivedly; nejde o nic osobního a analytickým nástrojům to neposíláme. Souhlas můžete kdykoli odvolat odkazem „Nastavení cookies“ v patičce webu nebo v nabídce účtu v aplikaci; podrobnosti uvádějí Zásady používání cookies.",
  s4Title: "4. Zabezpečení osobních údajů",
  securityItems: [
    [
      "Šifrování v klidu:",
      "AES-GCM šifrování na úrovni polí prostřednictvím klíčů odvozených PBKDF2",
    ],
    ["Šifrování při přenosu:", "HTTPS/TLS pro veškerou komunikaci"],
    [
      "Row Level Security:",
      "PostgreSQL RLS — uživatelé mají přístup pouze ke svým datům nebo sdíleným datům organizace",
    ],
    ["Auditní záznamy:", "logování všech citlivých operací"],
    [
      "Ochrana přihlášení:",
      "CAPTCHA (Cloudflare Turnstile) a omezení počtu požadavků (rate limiting)",
    ],
    ["Zálohy:", "denní šifrované zálohy uchovávané 30 dní"],
  ],
  s5Title: "5. Doba uchovávání údajů",
  retentionItems: [
    ["Údaje aktivního účtu:", "po celou dobu trvání účtu"],
    [
      "Po skončení předplatného nebo zkušební doby:",
      "12 měsíců v režimu pouze pro čtení (prohlížení a export), poté smazání nejdříve 30 dní po upozornění e-mailem",
    ],
    [
      "Po smazání účtu:",
      "smazání údajů v databázi a souborů dokladů a log evidovaných v Platformě bez zbytečného odkladu, nejpozději do 30 dnů; ze záloh do dalších 30 dnů. Soubory, které již nejsou přiřazeny k žádnému záznamu, smažeme do 30 dnů na žádost zaslanou na info@idex-corp.com",
    ],
    [
      "Účetní doklady o platbách za předplatné:",
      "po dobu, kterou stanoví účetní a daňové předpisy",
    ],
    ["Auditní záznamy:", "12 měsíců od vytvoření"],
    ["Protokoly zpracování e-mailů:", "6 měsíců"],
    ["Statistiky návštěvnosti (Google Analytics 4, PostHog):", "nejdéle 14 měsíců"],
  ],
  warnTitle: "Upozornění:",
  warnBody:
    "Pokud Uživatel využívá Platformu k evidenci účetních dokladů, je na jeho vlastní odpovědnosti zajistit archivaci dokladů po zákonnou dobu dle zákona č. 563/1991 Sb. (zpravidla 5–10 let). Provozovatel doporučuje před zrušením účtu provést export dat.",
  s6Title: "6. Práva subjektu údajů",
  rightsIntro: "V souladu s GDPR máte následující práva:",
  rightsItems: [
    "Právo na přístup (čl. 15 GDPR) — získat potvrzení o zpracování a kopii údajů",
    "Právo na opravu (čl. 16 GDPR) — opravit nepřesné údaje",
    'Právo na výmaz (čl. 17 GDPR) — „právo být zapomenut"',
    "Právo na omezení zpracování (čl. 18 GDPR)",
    "Právo na přenositelnost údajů (čl. 20 GDPR) — export dat v JSON formátu",
    "Právo vznést námitku (čl. 21 GDPR) — proti zpracování na základě oprávněného zájmu",
    "Právo odvolat souhlas — kdykoli, bez vlivu na zákonnost předchozího zpracování",
  ],
  rightsComplaintBefore: "Právo podat stížnost — u Úřadu pro ochranu osobních údajů (",
  rightsComplaintAfter: ")",
  rightsContactBefore: "Svá práva můžete uplatnit zasláním e-mailu na ",
  rightsContactAfter: ". Na žádost odpovíme bez zbytečného odkladu, nejpozději do 30 dnů.",
  s7Title: "7. Export a smazání dat",
  s7p1: "Platforma umožňuje úplný export uživatelských dat ve formátu JSON (transakce, faktury, účtenky, klienti, párování, auditní záznamy).",
  s7p2: "Smazání účtu a dat je možné prostřednictvím nastavení Platformy nebo žádostí na e-mail Správce.",
  s8Title: "8. Automatizované rozhodování a profilování",
  s8p1: "Platforma využívá AI funkce pro extrakci dat z dokladů a párování transakcí. Jedná se o automatizované zpracování, nikoli však o automatizované rozhodování s právními účinky ve smyslu čl. 22 GDPR.",
  s8p2: "Údaje přečtené z dokladů Platforma rovnou uloží a Uživatel je může kdykoli opravit. Párování, jehož skóre shody dosáhne alespoň 70 ze 100, Platforma použije automaticky, bez potvrzení Uživatelem; na základě ověřené příchozí platby může také označit vystavenou fakturu jako uhrazenou. Párování dokladu s platbou lze jedním kliknutím zrušit; fakturu označenou jako uhrazenou vrátíte změnou jejího stavu. Uživatel je povinen výsledky před dalším použitím zkontrolovat.",
  s9Title: "9. Změny zásad",
  s9p1: "O změnách těchto Zásad budeme informovat prostřednictvím e-mailu nebo oznámení v Platformě. Aktuální znění je vždy dostupné na Platformě.",
  s10Title: "10. Kontakt",
  emailLabel: "E-mail:",
  closingPlaceDate: "V Praze dne 25. října 2026",
};

const en: PrivacyLegal = {
  heroPill: "Effective 25 October 2026",
  title: "Privacy Policy",
  subtitle: "Zettl platform",
  controllerTitle: "Data controller",
  controllerIntro: "The controller of personal data is the operator of the Zettl platform:",
  vatStatus: "not a VAT payer",
  contactLabel: "Contact:",
  commercialRegister:
    "Registered in the Commercial Register kept by the Municipal Court in Prague, Section C, Insert 379847",
  s1Title: "1. Scope of processed data",
  s1_1Title: "1.1 Identification and contact data",
  s1_1Items: [
    "First name, surname, email address",
    "Company name, business address (street, city, postal code, country)",
    "Company ID, VAT ID, VAT payer status",
    "Language and currency preferences, display preferences",
    "When you sign in with Google: your name, email address and profile picture, as passed to us by Google",
  ],
  s1_2Title: "1.2 Financial data",
  s1_2Items: [
    "Bank transaction records (date, description, amount, currency, category)",
    "Invoices including line items, pro forma invoices, credit notes",
    "Receipt files and extracted metadata",
    "Bank API credentials (encrypted tokens)",
    "Client records (encrypted)",
    "Budget line items and expense records",
    "Billing details and subscription payment history (once payments launch); payment card details are processed by Stripe directly and the controller never sees them",
  ],
  s1_3Title: "1.3 Technical and operational data",
  s1_3Items: [
    "IP addresses (audit logs)",
    "User agent strings (audit logs)",
    "Email processing logs (sender, subject, attachment count, status)",
    "Synchronisation error logs",
    "Rate limit counters",
    "Application error reports and recordings of sessions that hit an error, with text masked (Sentry)",
    "Traffic and usage statistics for the website and app, only with your consent (Google Analytics 4, PostHog)",
  ],
  s2Title: "2. Purposes of processing and legal basis",
  thPurpose: "Purpose",
  thLegalBasis: "Legal basis (GDPR)",
  purposeRows: [
    ["Providing the Platform services", "Art. 6(1)(b) — performance of a contract"],
    ["Registration and account management", "Art. 6(1)(b) — performance of a contract"],
    ["Bank account synchronisation", "Art. 6(1)(a) — consent"],
    ["AI processing of documents", "Art. 6(1)(b) — performance of a contract"],
    ["Security and abuse prevention", "Art. 6(1)(f) — legitimate interests"],
    ["Audit logs", "Art. 6(1)(f) — legitimate interests"],
    ["Communication with the user", "Art. 6(1)(b) — performance of a contract"],
    [
      "Subscription payments and accounting documents (once payments launch)",
      "Art. 6(1)(b) — performance of a contract; Art. 6(1)(c) — legal obligation (accounting)",
    ],
    ["Detecting and fixing application errors", "Art. 6(1)(f) — legitimate interests"],
    ["Traffic and usage statistics for the website and app", "Art. 6(1)(a) — consent"],
  ],
  s3Title: "3. Recipients and processors",
  s3Intro: "Personal data may be disclosed to the following categories of recipients (processors):",
  thProcessor: "Processor",
  thPurposeCol: "Purpose",
  thDataLocation: "Data location",
  processorRows: [
    ["Supabase, Inc.", "Database, sign-in, file storage, server functions", "EU (Ireland)"],
    [
      "Vercel Inc.",
      "Web hosting, CDN, traffic and performance statistics (Vercel Analytics, Speed Insights), AI Gateway",
      "EU/USA",
    ],
    ["Google LLC (Gemini)", "AI document reading and matching (via Vercel AI Gateway)", "EU/USA"],
    ["Google LLC (Fonts)", "Web font loading (Sora)", "EU/USA"],
    [
      "Cloudflare, Inc.",
      "Receiving e-mails with documents; encrypted nightly backups (R2, kept 30 days)",
      "EU/USA",
    ],
    ["Cloudflare, Inc. (Turnstile)", "Bot protection for sign-in and forms (CAPTCHA)", "EU/USA"],
    [
      "Resend, Inc.",
      "Sending e-mails (registration and sign-in, trial ending reminders, team invitations, replies to the contact form)",
      "EU/USA",
    ],
    ["Functional Software, Inc. (Sentry)", "Application error monitoring", "EU (Germany)"],
    [
      "Google Ireland Limited (Google Analytics 4)",
      "Traffic and usage statistics for the website and app (only with your consent)",
      "EU/USA",
    ],
    [
      "PostHog, Inc.",
      "Usage statistics for the website and app (only with your consent)",
      "EU (Germany)",
    ],
    [
      "GitHub, Inc.",
      "Running the nightly backup (data stays on the runner only briefly and is encrypted before storage)",
      "USA",
    ],
    [
      "Stripe Payments Europe, Ltd.",
      "Subscription payments (once payments launch); for fraud prevention and its regulatory obligations Stripe acts as an independent controller",
      "EU (Ireland)/USA",
    ],
  ],
  s3Footer:
    "The database and uploaded files are stored in the EU (Ireland), error reports in the EU (Germany). Some processors, however, are US companies or run global infrastructure, so personal data may be transferred outside the EU by processors marked “EU/USA” or “USA”, and by US-based processors even where data is stored in the EU. Such transfers rely on the European Commission's adequacy decision (EU-US Data Privacy Framework) for certified recipients and otherwise on standard contractual clauses (Art. 46 GDPR). When web fonts are loaded from Google Fonts, Google LLC processes the visitor's IP address. Our sign-in and registration forms use Cloudflare Turnstile: to tell people from bots, Cloudflare processes the IP address and technical browser and device data. The legal basis for both is our legitimate interest in keeping the service secure and working (Art. 6(1)(f) GDPR). We run Google Analytics 4 and PostHog only if you agree to them in the cookie bar (Art. 6(1)(a) GDPR). They may then store cookies or other identifiers in your browser and process your IP address, browser and device data and which pages and features you use. With your consent we also store with your company the campaign (utm parameters) and the host name of the site that led to its registration; nothing personal, and it is not sent to analytics tools. You can withdraw your consent at any time through “Nastavení cookies” (cookie settings) in the site footer or the account menu in the app; the Cookie Policy has the details.",
  s4Title: "4. Security of personal data",
  securityItems: [
    ["Encryption at rest:", "AES-GCM field-level encryption using PBKDF2-derived keys"],
    ["Encryption in transit:", "HTTPS/TLS for all communication"],
    [
      "Row Level Security:",
      "PostgreSQL RLS — users only access their own data or organisation-shared data",
    ],
    ["Audit logs:", "logging of all sensitive operations"],
    ["Sign-in protection:", "CAPTCHA (Cloudflare Turnstile) and rate limiting"],
    ["Backups:", "daily encrypted backups kept for 30 days"],
  ],
  s5Title: "5. Retention periods",
  retentionItems: [
    ["Active account data:", "for the lifetime of the account"],
    [
      "After the subscription or trial ends:",
      "12 months in read-only mode (viewing and export), then deletion no earlier than 30 days after an e-mail notice",
    ],
    [
      "After account deletion:",
      "database records and the document and logo files the Platform holds are deleted without undue delay, within 30 days at the latest; from backups within a further 30 days. Files no longer linked to any record are deleted within 30 days of a request sent to info@idex-corp.com",
    ],
    [
      "Accounting documents for subscription payments:",
      "for the period required by accounting and tax law",
    ],
    ["Audit logs:", "12 months from creation"],
    ["Email processing logs:", "6 months"],
    ["Traffic statistics (Google Analytics 4, PostHog):", "at most 14 months"],
  ],
  warnTitle: "Note:",
  warnBody:
    "If you use the Platform to keep accounting records, you are responsible for archiving documents for the statutory period under Act No. 563/1991 Coll. (typically 5–10 years). We recommend exporting data before cancelling your account.",
  s6Title: "6. Data subject rights",
  rightsIntro: "Under the GDPR you have the following rights:",
  rightsItems: [
    "Right of access (Art. 15 GDPR) — confirmation of processing and a copy of data",
    "Right to rectification (Art. 16 GDPR) — correct inaccurate data",
    "Right to erasure (Art. 17 GDPR) — “right to be forgotten”",
    "Right to restriction of processing (Art. 18 GDPR)",
    "Right to data portability (Art. 20 GDPR) — export in JSON format",
    "Right to object (Art. 21 GDPR) — to processing based on legitimate interests",
    "Right to withdraw consent — at any time, without affecting prior lawful processing",
  ],
  rightsComplaintBefore:
    "Right to lodge a complaint — with the Czech Office for Personal Data Protection (",
  rightsComplaintAfter: ")",
  rightsContactBefore: "You can exercise your rights by emailing ",
  rightsContactAfter: ". We will respond without undue delay, within 30 days at the latest.",
  s7Title: "7. Export and deletion",
  s7p1: "The Platform allows full export of user data in JSON format (transactions, invoices, receipts, clients, matching, audit logs).",
  s7p2: "Account and data deletion are available through Platform settings or by request to the controller’s email.",
  s8Title: "8. Automated decision-making and profiling",
  s8p1: "The Platform uses AI features to extract data from documents and match transactions. This is automated processing, not automated decision-making with legal effects within the meaning of Art. 22 GDPR.",
  s8p2: "Data read from documents is saved straight away, and the user can correct it at any time. A match with a score of at least 70 out of 100 is applied automatically, without the user's confirmation; based on a verified incoming payment, the Platform may also mark an issued invoice as paid. A match of a document with a payment can be undone with one click; an invoice marked as paid is reverted by changing its status. Users must check the results before relying on them.",
  s9Title: "9. Changes to this policy",
  s9p1: "We will inform you of changes by email or notices on the Platform. The current version is always available on the Platform.",
  s10Title: "10. Contact",
  emailLabel: "Email:",
  closingPlaceDate: "Prague, 25 October 2026",
};

export const privacyTranslations: Record<Language, PrivacyLegal> = {
  cs,
  en,
};
